import React, { useEffect, useState, useRef } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import Editor from 'ckeditor5-custom-build/build/ckeditor'

import { useBaseContext } from 'context/base'
import TemplateAdmin from 'component/admin/TemplateAdmin'
import { Container, Header } from 'component/template'
import BackButton from 'component/button/Back'
import SubmitButton from 'component/button/Submit'
import { FormSwitch } from 'component/forms'
import { minimalConfig } from 'util/ckeditor-config'
import useEditContent from './hook'
import {
  FormInputName,
  FormInputUrutan,
  FormSelectSection,
  FormSelectPrecontent,
  FormInputTanggal,
  FormDurasi,
  FormActiveStatus,
  FormSelectFeedback,
  FormGroupKecil,
  FormActiveKey,
  FormInputDesc,
  FormInputEndedAt,
  FormTextArea,
  FormSetToBeScore,
  FormSelectStudent,
} from 'component/content-form'
import { getUsersContentSpesificStudent } from 'util/array'

export default function EditContentText() {
  /* ========================================== Helpers ========================================= */
  const history = useHistory()
  const location = useLocation()
  const params = useParams()

  /* ======================================= Consume Hook ======================================= */
  const { getContentTakeAway } = useEditContent()

  /* ====================================== Consume Context ===================================== */
  const { getRequest, postRequest, putRequest } = useBaseContext()

  /* ========================================= Constants ======================================== */
  const existingContentData = location.state?.dataContent
  const sections = location.state.sectionList ? location.state.sectionList : []
  const contentId = existingContentData.id || ''
  const links = [
    { url: '/kelas', name: 'Mata Pelajaran' },
    { url: `/kelas/${location.state.kelasId}`, name: 'Overview' },
  ]

  /* ======================================= Local States ======================================= */
  const [existingContents, setExistingContents] = useState([])
  const [isContentTakeAwayExist, setIsContentTakeAwayExist] = useState(false)

  // FORM INPUT STATE
  const [namaKonten, setNamaKonten]     = useState(existingContentData?.title || '')
  const [sectionId, setSectionId]       = useState(existingContentData?.section_id || '')
  const [preContentId, setPrecontentId] = useState(existingContentData?.precontent_id || '')
  const [active, setActive]             = useState(existingContentData?.active ? 1 : 0)
  const [durasi, setDurasi]             = useState(existingContentData?.durasi ? existingContentData.durasi / 60 : '')
  const [displayAt, setDisplayAt]       = useState(existingContentData?.display_at || '')
  const [contentText, setContentText]   = useState(existingContentData?.content_text || '')
  const [urutan, setUrutan]             = useState(existingContentData?.urutan || '')
  const [feeds, setFeeds]               = useState([])
  const [feedId, setFeedId]             = useState(null)
  const [isFeedActive, setIsFeedActive] = useState(0)
  const [enableGroup, setEnableGroup]   = useState(!!existingContentData?.group_status)
  const [maxGroup, setMaxGroup]         = useState(existingContentData?.group_max || '')
  const [descGroup, setDescGroup]       = useState(existingContentData?.group_description || '')
  const [endedAt, setEndedAt]           = useState(existingContentData?.ended_at || '')
  const [description, setDescription]   = useState(existingContentData?.deskripsi || '')
  const [apresiasi, setApresiasi]       = useState(existingContentData?.apresiasi || '')
  const [rating, setRating]             = useState(existingContentData?.rating ? 1 : 0)
  const [toBeScore, setToBeScore]   = useState({
    isScore: existingContentData.is_score,
    isKeterampilan: existingContentData.is_keterampilan,
    isPengetahuan: existingContentData.is_pengetahuan,
    selectedTypeScore: existingContentData.type_score_id,
    isTypeScoreActive: true,
  })
  const [selectedStudents, setSelectedStudents] = useState(() => getUsersContentSpesificStudent(existingContentData))

  // FORM VALIDATION
  const [isNamaInvalid, setIsNamaInvalid]       = useState(false)
  const [isUrutanInvalid, setIsUrutanInvalid]   = useState(false)
  const [isSectionInvalid, setIsSectionInvalid] = useState(false)
  const [isContenInvalid, setIsContentInvalid]  = useState(false)
  const [isMaxGroupValid, setIsMaxGroupValid]   = useState(true)

  const [enableGroupKey, setEnableGroupKey] = useState(false)
  const [prerequisite, setPrerequisite]     = useState('')

  /* =========================================== Refs =========================================== */
  const refInputName     = useRef()
  const refInputUrutan   = useRef()
  const refSelectSection = useRef()
  const refMaxGroup      = useRef()

  function getExistingContentsBySelectedSection() {
    if (!sectionId) {
      setExistingContents([])
    } else {
      sections.forEach((section) => {
        if (section.id === sectionId) {
          setExistingContents(section.contents)
        }
      })
    }
  }

  function handleChangeEnableGroupKey() {
    if (enableGroupKey) {
      setEnableGroupKey(false)
    } else {
      setEnableGroupKey(true)
    }
  }

  async function getFeedsList() {
    const response = await getRequest('feed')
    if (response) {
      const options = response.data.map((item) => ({
        value: item.id,
        label: item.description,
      }))
      setFeeds(options)
      initSelectedFeed(options)
    } else {
      console.error('ERROR', response)
    }
  }

  function initSelectedFeed(feedList) {
    for (let feed of feedList) {
      if (feed.value === existingContentData.feedback) {
        setFeedId({ value: feed.value, label: feed.label })
        break
      }
    }
  }

  function handleChangeName(event) {
    setNamaKonten(event.target.value)
  }

  function handleChangeUrutan(event) {
    setUrutan(event.target.value)
  }

  function handleChangeSection(event) {
    setSectionId(event.target.value)
  }

  function handleChangePrecontent(event) {
    setPrecontentId(event.target.value)
  }

  function handleChangeActive() {
    if (active === 1) {
      setActive(0)
    }
    if (active === 0) {
      setActive(1)
    }
  }

  function handleChangeDate(event) {
    setDisplayAt(event.target.value)
  }

  function handleChangeDurasi(event) {
    setDurasi(event.target.value)
  }

  function handleChangeFeedback(val) {
    setFeedId(val)
  }

  function handleChangeEnableGroup() {
    if (enableGroup) {
      setEnableGroup(false)
      setMaxGroup(
        existingContentData.group_max ? existingContentData.group_max : '',
      )
      setIsMaxGroupValid(true)
    } else {
      setEnableGroup(true)
      setMaxGroup(
        existingContentData.group_max ? existingContentData.group_max : '',
      )
      if (existingContentData.group_max) {
        setIsMaxGroupValid(true)
      } else {
        setIsMaxGroupValid(false)
      }
    }
  }

  function handleChangeMaxGroup(event) {
    if (event.target.value) {
      const absolute = Math.abs(event.target.value)
      const integer = Math.round(absolute)
      setMaxGroup(integer)
      if (integer <= 0) {
        setIsMaxGroupValid(false)
      } else {
        setIsMaxGroupValid(true)
      }
    } else {
      setMaxGroup('')
      setIsMaxGroupValid(false)
    }
  }

  function handleChangeDescGroup(event) {
    setDescGroup(event.target.value)
  }

  function handleChangeDesc(event) {
    setDescription(event.target.value)
  }

  function handleChangeEndedAt(event) {
    setEndedAt(event.target.value)
  }

  function handleChangeApresiasi(event) {
    setApresiasi(event.target.value)
  }

  function handleChangeRating(value) {
    setRating(value ? 1 : 0)
  }

  function toBeScoreValidation() {
    if (toBeScore.isScore) {
      return (toBeScore.isKeterampilan === true || toBeScore.isPengetahuan === true) && toBeScore.selectedTypeScore
    }

    return true
  }

  function handleValidation() {
    const newUrutan = urutan ? Math.abs(parseInt(urutan)) : 0
    const durasiDetik = durasi * 60
    const feedback = isFeedActive === 1 ? feedId : ''
    console.log({isFeedActive, feedId})

    if (namaKonten && sectionId && contentText && urutan && isMaxGroupValid && toBeScoreValidation()) {
      setIsNamaInvalid(false)
      setIsUrutanInvalid(false)
      setIsSectionInvalid(false)
      setIsContentInvalid(false)
      handleSubmitForm(
        contentId,
        contentText,
        namaKonten,
        durasiDetik,
        sectionId,
        newUrutan,
        active,
        displayAt,
        preContentId,
        feedback.value,
        maxGroup,
        descGroup,
        endedAt,
        description,
        apresiasi,
        rating,
      )
    } else {
      if (!isMaxGroupValid) {
        if (refMaxGroup) {
          refMaxGroup.current.focus()
        }
      }

      if (!sectionId) {
        setIsSectionInvalid(true)
        refSelectSection.current.focus()
      } else {
        setIsSectionInvalid(false)
      }

      if (!urutan) {
        setIsUrutanInvalid(true)
        refInputUrutan.current.focus()
      } else {
        setIsUrutanInvalid(false)
      }

      if (!contentText) {
        setIsContentInvalid(true)
      }

      if (!namaKonten) {
        setIsNamaInvalid(true)
        refInputName.current.focus()
      } else {
        setIsNamaInvalid(false)
      }

      window.notification('Mohon', 'lengkapi data')

      if (!toBeScoreValidation()) {
        window.notification('Mohon', 'lengkapi juga data "Jadikan Sebagai Penilaian"')
      }
    }
  }

  async function handleSubmitForm(
    id,
    contentText,
    title,
    durasi,
    sectionId,
    urutan,
    active,
    displayAt,
    preContentId,
    feedId,
    maxGroup,
    descGroup,
    endedAt,
    description,
    apresiasi,
    rating,
  ) {
    let form = new FormData()
    form.append('id', id)
    form.append('content_type', 1)
    form.append('content_text', contentText)
    form.append('title', title)
    form.append('durasi', durasi)
    form.append('section_id', sectionId)
    form.append('urutan', urutan)
    form.append('active', active)
    form.append('rating', rating)
    form.append('precontent_id', preContentId)
    form.append('display_at', displayAt)
    form.append('ended_at', endedAt)
    form.append('deskripsi', description)
    form.append('apresiasi', apresiasi)

    form.append('is_score', !!toBeScore.isScore)

    if (toBeScore.isScore) {
      form.append('is_keterampilan', !!toBeScore.isKeterampilan)
      form.append('is_pengetahuan', !!toBeScore.isPengetahuan)
      form.append('type_score_id', toBeScore.selectedTypeScore)
    }
    if (Array.isArray(selectedStudents) && !!selectedStudents.length) {
      selectedStudents.forEach((selectedStudent, selectedStudentIndex) => {
        form.append(`spesifik_user_id[${selectedStudentIndex}]`, selectedStudent.id)
      })
    }

    if (feedId) {
      form.append('feedback', feedId)
    } else {
      form.append('feedback', '')
    }

    if (enableGroup) {
      form.append('group_status', 1)
    } else {
      form.append('group_status', 0)
    }

    if (maxGroup) {
      form.append('group_max', maxGroup)
    }

    if (descGroup) {
      form.append('group_description', descGroup)
    }

    const response = await putRequest('contents', form)
    if (response) {
      if (enableGroupKey) {
        let formkey = new FormData()
        formkey.append('description', prerequisite)
        formkey.append('content_id', id)

        const response1 = isContentTakeAwayExist
          ? await putRequest('contentTakeAway', formkey)
          : await postRequest('contentTakeAway', formkey)

        if (response1) {
          window.notification('Materi', 'berhasil diubah', 'success')
          history.goBack()
        }
      } else {
        window.notification('Materi', 'berhasil diubah', 'success')
        history.goBack()
      }
    }
  }

  /* ========================================== Effects ========================================= */
  useEffect(() => {
    getContentTakeAway(contentId).then((res) => {
      if (res.data) {
        setPrerequisite(res.data.description)
        setEnableGroupKey(true)
        setIsContentTakeAwayExist(true)
      }
    })

    getFeedsList()
  }, [])

  useEffect(() => {
    getExistingContentsBySelectedSection()
  }, [sectionId])

  useEffect(() => {
    if (existingContentData.feedback !== null) {
      setIsFeedActive(1)
    }
  }, [existingContentData.feedback])

  /* ========================================== Output ========================================== */
  return (
    <TemplateAdmin>
      <Container>
        <Header
          pageName="Edit Materi Teks"
          linkName="Materi Teks"
          links={links}
        />
        
        <div className="row">
          <div className="col">
            <div className="card">
              <div className="card-header">
                <h5>
                  <i className="fas fa-file-alt mr-1" />
                  Informasi Teks
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col">
                    <form>
                      {/* Input Title */}
                      <FormInputName
                        label="Nama Materi"
                        placeholder="Nama Materi"
                        namaKonten={namaKonten}
                        isNamaInvalid={isNamaInvalid}
                        handleChangeName={handleChangeName}
                        refInputName={refInputName}
                      />

                      {/* Input Konten Teks */}
                      <div className="form-group mb-5 row">
                        <label className="col-sm-3 col-form-label">
                          <span className="mr-1">Konten Teks</span>
                          {/* <span className="text-danger">*</span> */}
                        </label>
                        <div className="col-sm-9">
                          <CKEditor
                            editor={Editor}
                            data={contentText}
                            config={minimalConfig}
                            onChange={(_, editor) => {
                              const data = editor.getData()
                              setContentText(data)
                            }}
                          />
                          {isContenInvalid ? (
                            <label className="col small form-text text-danger">
                              Konten tidak boleh kosong.
                            </label>
                          ) : null}
                        </div>
                      </div>

                      {/* Input urutan */}
                      <FormInputUrutan
                        urutan={urutan}
                        handleChangeUrutan={handleChangeUrutan}
                        refInputUrutan={refInputUrutan}
                        isUrutanInvalid={isUrutanInvalid}
                      />

                      {/* Input deskripsi */}
                      <FormInputDesc
                        description={description}
                        handleChangeDesc={handleChangeDesc}
                      />

                      {/* Input Apresiasi */}
                      <FormTextArea
                        id="formInputApresiasi"
                        label="Apresiasi"
                        placeholder="Apresiasi/pesan setelah siswa menyelesaikan materi (opsional)"
                        className="mb-5"
                        value={apresiasi}
                        handleChange={handleChangeApresiasi}
                      />

                      {/* Select Section */}
                      <FormSelectSection
                        sections={sections}
                        existingSectionId={sectionId}
                        handleChangeSection={handleChangeSection}
                        refSelectSection={refSelectSection}
                        isSectionInvalid={isSectionInvalid}
                      />

                      {/* Select Precontent */}
                      <FormSelectPrecontent
                        existingContents={existingContents}
                        existingPrecontentId={preContentId}
                        existingContentId={contentId}
                        handleChangePrecontent={handleChangePrecontent}
                      />

                      {/* Input Tanggal */}
                      <FormInputTanggal
                        existingDisplayAt={displayAt}
                        handleChangeDate={handleChangeDate}
                      />

                      {/* Input ended at */}
                      <FormInputEndedAt
                        endedAt={endedAt}
                        handleChangeEndedAt={handleChangeEndedAt}
                      />

                      {/* Input Durasi */}
                      <FormDurasi
                        existingDurasi={durasi}
                        handleChangeDurasi={handleChangeDurasi}
                      />

                      {/* Rating */}
                      <FormSwitch
                        id="formRating"
                        label="Rating Materi"
                        value={rating}
                        handleChange={handleChangeRating}
                      />

                      {/* Select Feedback */}
                      <FormSelectFeedback
                        existingFeeds={feeds}
                        existingFeedId={feedId}
                        isFeedActive={isFeedActive}
                        setIsFeedActive={setIsFeedActive}
                        handleChangeFeeds={handleChangeFeedback}
                        getFeedList={getFeedsList}
                      />

                      {/* Swicth Group Kecil */}
                      <FormGroupKecil
                        enableGroup={enableGroup}
                        handleChangeEnableGroup={handleChangeEnableGroup}
                        maxGroup={maxGroup}
                        descGroup={descGroup}
                        handleChangeMaxGroup={handleChangeMaxGroup}
                        handleChangeDescGroup={handleChangeDescGroup}
                        isMaxGroupValid={isMaxGroupValid}
                        refMaxGroup={refMaxGroup}
                      />

                      {/* Select Specific Students */}
                      <FormSelectStudent
                        selectedStudents={selectedStudents}
                        setSelectedStudents={setSelectedStudents}
                        subMapelId={params?.kelasId}
                        originalSelectedStudents={getUsersContentSpesificStudent(existingContentData)}
                        isDirectSave={false}
                        isWithFooter={false}
                        listSelectedStudentsProps={{
                          listStudentsActions: ['rollback', 'clear-all'],
                        }}
                      />

                      {/* Select To Be Score Or Not */}
                      <FormSetToBeScore
                        value={toBeScore}
                        handleChange={setToBeScore}
                      />

                      {/* Select Active Status */}
                      <FormActiveStatus
                        existingStatus={active}
                        handleChangeStatus={handleChangeActive}
                      />

                      <FormActiveKey
                        enableGroupKey={enableGroupKey}
                        handleChangeEnableGroupKey={handleChangeEnableGroupKey}
                        prerequisite={prerequisite}
                        setPrerequisite={setPrerequisite}
                      />

                      <div className="form-group">
                        <BackButton />
                        <SubmitButton
                          onClick={() => handleValidation()}
                          text="Simpan"
                        />
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </TemplateAdmin>
  )
}
